import { BaseLogger } from '../BaseLogger';
import { consoleLogProcessor } from '../LogProcessor/ConsoleLogProcessor';
import { ITelemetryProperties } from '@fluidframework/common-definitions';
import { LogProcessor, UserContext, AriaInfo, OtelLogProcessor } from '../LogProcessor';
import { OfficeFluidAriaInfo, OfficeFluidTestAriaInfo } from './OfficeFluidAriaInfo';
import { HostContext, Audience, Event } from '@fluidx/office-fluid-types';

/**
 * Host metadata to be logged as a single log statement during a particular session
 */
export interface HostMetadata {
  /**
   * Ring information specific to host app's concepts, which may not be the same as FFX's audience concept
   */
  hostRing?: string;
  /**
   * Object containing flights on the host app related to Fluid Experiences.
   * Please do not include flights that do not impact Fluid Experiences to avoid excessive logging
   */
  hostFlights?: ITelemetryProperties;
}

/**
 * Parameters to the Office Fluid Logger
 */
interface OfficeFluidLoggerConfig {
  /**
   * Host specific fields that get logged with every event
   */
  hostContextProps: HostContext;
  /**
   * Host specific metadata that gets logged as a single log statement during a particular session.
   * This is different from the persistentProps in that the persistentProps get sent with every telemetry event.
   * We expect all host specific properties to come in as hostMetadata. Please reach out to FFX team to discuss if you think a host specific property should lie in persistentProps.
   */
  hostMetadata?: HostMetadata;
  /**
   * Properties that get logged with every event
   */
  persistentProps?: ITelemetryProperties;
  /**
   * The namespace of the logger
   */
  namespace?: string;
  /**
   * The parameter that defines if hosts want to delay the call to load the OtelLogProcessor.
   * Hosts are expected to call the loadFull method if this param is set to true
   */
  delayLoadLogger?: boolean;
}

/**
 * Implementation of the Logger interface that buffers events and delegates events to a LogProcessor once it is fully loaded.
 */
export class OfficeFluidLogger extends BaseLogger {
  private logProcessor: LogProcessor;
  private readonly ariaInfo: AriaInfo;
  protected readonly audience: Audience;
  protected forceTelemetryToConsole = false;

  /**
   * Constructor to initialize the Logger.
   */
  constructor(config: OfficeFluidLoggerConfig) {
    super(
      {
        ...config.persistentProps,
        hostCorrelationId: config.hostContextProps.hostCorrelationId,
        // HostScenarioName = <LifeCycle>_<Event/ScenarioName>_<EntryPoint>
        hostScenarioName: [
          config.hostContextProps.hostScenarioLifecycle,
          config.hostContextProps.hostScenarioName,
          config.hostContextProps.hostScenarioEntryPoint
        ]
          .filter((item) => item) // Filter only to non-empty values
          .join('_'),
        hostName: config.hostContextProps.hostName
      },
      config.namespace
    );

    const { hostContextProps, delayLoadLogger } = config;

    this.audience = hostContextProps.audience;
    this.ariaInfo = this.getAriaInfo();

    try {
      this.forceTelemetryToConsole = localStorage.getItem('debugPurple') === '1';
    } catch {
      // Storage might not be available in some scenarios due to tracking protection.
      // Tracking protection settings are immutable within an app session so it is okay to detect
      // that state only once during the logger's lifetime.
    }

    // The OtelLogProcessor has the logic to push events into a queue, so consumers of OtelJs APIs can delay load the one ds sink
    // Create an OtelLogProcessor right away, and pass the necessary information to the logProcessor
    this.logProcessor = new OtelLogProcessor(this.ariaInfo, {
      appPlatform: hostContextProps.hostAppPlatform,
      appName: hostContextProps.hostAppName,
      appVersion: hostContextProps.hostAppVersion,
      user: hostContextProps.userContext.user,
      sessionId: hostContextProps.hostSessionId,
      audience: hostContextProps.audience,
      uiLanguage: hostContextProps.hostLocaleContext?.uiLanguage
    });

    // If delayLoadLogger is not passed or set as false, load the OneDSSink right away
    if (!delayLoadLogger) {
      this.loadFull().catch(() => {
        // We do not need to explicitly handle the catch here as loadFull handles it internally
      });
    }

    if (config.hostMetadata) {
      const { hostMetadata } = config;
      const hostFlights = hostMetadata.hostFlights;

      this.sendWithNamespace(
        {
          eventName: 'HostMetadata',
          hostRing: hostMetadata.hostRing,
          hostFlights: hostFlights && JSON.stringify(hostFlights),
          category: 'Generic'
        },
        'HostMetadata' // namespace
      );
    }
  }

  /**
   * Calls the logProcessor's loadFull method, so the OneDsSink is loaded and events in the telemetryLogger's queue are sent to the pipeline.
   * The host application is responsible for calling this method if delayLoadLogger is set to true.
   * This is useful when hosts want to instantiate the logger but want to reduce the initial bits pulled down.
   * If delayLoadLogger is undefined or set to false, the OfficeFluidLogger calls the loadFull method from the constructor
   * Invoking this for a second or higher time will be a no-op.
   */
  public async loadFull() {
    // Adding this back because we need to continue to expose loadFull as it was public method, but this can call loadFull from the logProcessor
    try {
      await this.logProcessor.loadFull();
    } catch {
      // We do not need to explicitly handle the catch here as logProcessor.loadFull handles it internally
    }
  }

  /**
   * Method that returns the aria info for the tenant to route the telemetry to
   */
  private getAriaInfo() {
    if (this.isTestTelemetryByStorageHooks() || this.isTestTelemetryByAudience()) {
      return OfficeFluidTestAriaInfo;
    }

    return OfficeFluidAriaInfo;
  }

  private isTestTelemetryByAudience(): boolean {
    return this.audience === 'Local' || this.audience === 'Automation';
  }

  private isTestTelemetryByStorageHooks(): boolean {
    // Known developer support for "sideloading" have been retroactively collected here to assist in mitigating their
    // impact in telemetry when the original feature does not fully account for disinformation that may be caused.

    let developerHookDetected: boolean = false;
    try {
      developerHookDetected =
        !!localStorage.getItem('AllowListOverride') ||
        !!localStorage.getItem('OfficeContainerEnvironmentOverride') ||
        !!localStorage.getItem('FluidCodeDetailsOverride');
    } catch {
      // Any issue attempting to detect support for or availabilty of either localStorage property or any part of the Web Storage API indicates there is not a developer hook to detect here.
    }

    return developerHookDetected;
  }

  /**
   * Sets special, well-known persistent fields for User Identity on the events logged through the logger.
   * These fields have to be logged in a certain way by the logProcessor.
   * For example, the OtelLogProcessor converts these fields such that it follows the Office Schema.
   */
  public setUserContext(context: UserContext) {
    this.logProcessor.setUserContext(context);
  }

  /**
   * Sets special, well-known persistent field for ECS eTag on the events logged through the logger.
   * This field have to be logged in a certain way by the logProcessor.
   * For example, the OtelLogProcessor converts these fields such that it follows the Office Schema.
   */
  public setSessionEcsETag(sessionEcsETag: string) {
    this.logProcessor.setSessionEcsETag(sessionEcsETag);
  }

  /**
   * Prevents new events from being added for transmission. It also batches all
   * events currently in the queue, and creates requests for them to be sent.
   * Make sure that this is called after logging events related to session end.
   */
  public shutdown = () => {
    this.logProcessor.shutdown();
  };

  protected logEvent(event: Event): void {
    // In addition to sending the event to the registered logProcessor, log to console if forceTelemetryToConsole is set to true.
    // TODO: Investigate using the debug library to do the styling in the future.
    this.forceTelemetryToConsole && consoleLogProcessor.logEvent(event);

    this.logProcessor.logEvent(event);
  }
}

// cspell:ignore otel
